/**
 * Authentication Store (Zustand)
 *
 * Cookie-based opaque session pattern (ported from colliers/cherie-app).
 * The browser holds an HttpOnly session_id cookie set by the backend; this
 * store keeps the user/role/permissions in memory only — nothing in
 * localStorage.
 *
 * Backwards compatibility: the public hooks (`useUser`, `useProfile`, `useRole`,
 * `useIsAuthenticated`, `useIsAdmin`) and store actions (`login`, `register`,
 * `logout`, `checkAuth`, `hasPermission`, `hasRole`, `isAdmin`) are preserved
 * so existing components keep compiling. The internal data shape now matches
 * the backend AuthMeResponse.
 */

import { create } from "zustand";

import {
  authApi,
  type AuthUser,
  type AuthRole,
  type AuthMeResponse,
} from "../services/auth.service";

// ─── Public Types (back-compat) ───────────────────────────────

/** Supabase Auth user surface (kept for back-compat). */
export interface User {
  id: string;
  email: string;
  email_confirmed_at?: string;
  created_at?: string;
  updated_at?: string;
  role?: string;
  /** @deprecated Tokens live server-side now; this is always undefined. */
  token?: string;
  /** @deprecated Use the subscription_types/user_subscriptions tables instead. */
  subscription_level?: string;
}

/** App-side profile shape (back-compat). */
export interface UserProfile {
  id: string;
  user_id: string;
  email: string;
  full_name: string;
  first_name?: string;
  last_name?: string;
  organization_id?: string | null;
  role_id?: string | null;
  session_valid_after?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Role {
  id?: string;
  role_name: string;
  role_type: string;
  description?: string;
  is_default?: boolean;
}

export interface Permission {
  id?: string;
  perm_name: string;
  description?: string;
}

// ─── Store ────────────────────────────────────────────────────

interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  role: Role | null;
  permissions: Permission[];

  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    full_name: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  setError: (error: string | null) => void;

  // Permission helpers
  hasPermission: (permission: string) => boolean;
  hasRole: (roleName: string) => boolean;
  isAdmin: () => boolean;
}

/** Convert the AuthMeResponse → the legacy {user, profile, role, permissions} shape. */
function applyAuthResponse(data: AuthMeResponse) {
  const u: AuthUser = data.user;
  const r: AuthRole = data.role;
  return {
    user: {
      id: u.id,
      email: u.email,
      email_confirmed_at: u.emailVerified
        ? new Date(0).toISOString()
        : undefined,
    } as User,
    profile: {
      id: u.id,
      user_id: u.id, // backend already maps profile.id; auth.users.id is server-only
      email: u.email,
      full_name: u.fullName,
      organization_id: u.organizationId,
    } as UserProfile,
    role: {
      role_name: r.name,
      role_type: r.isAdmin ? "admin" : "user",
    } as Role,
    permissions: data.permissions.map((p): Permission => ({ perm_name: p })),
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  role: null,
  permissions: [],
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,

  setError: (error) => set({ error }),

  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const data = await authApi.login(email, password);
      set({
        ...applyAuthResponse(data),
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Login failed",
        isLoading: false,
      });
      throw error;
    }
  },

  register: async (email, password, full_name) => {
    set({ isLoading: true, error: null });
    try {
      const result = await authApi.signup(email, password, full_name);

      if (!result.needsVerification) {
        // Auto-login: backend already set the cookie. Hydrate via /me.
        try {
          const me = await authApi.getCurrentUser();
          set({
            ...applyAuthResponse(me),
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
          });
          return;
        } catch {
          // Fall through — treat as needing verification
        }
      }

      // Needs email verification — leave unauthenticated
      set({ isLoading: false, isInitialized: true });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Registration failed",
        isLoading: false,
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Best effort — clear local state regardless
    }
    set({
      user: null,
      profile: null,
      role: null,
      permissions: [],
      isAuthenticated: false,
      error: null,
    });
  },

  checkAuth: async () => {
    set({ isLoading: true });
    try {
      const data = await authApi.getCurrentUser();
      set({
        ...applyAuthResponse(data),
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
      });
    } catch {
      set({
        user: null,
        profile: null,
        role: null,
        permissions: [],
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      });
    }
  },

  refreshPermissions: async () => {
    try {
      const { permissions } = await authApi.getPermissions();
      set({ permissions: permissions.map((p) => ({ perm_name: p })) });
    } catch {
      // ignore
    }
  },

  hasPermission: (permission) => {
    return get().permissions.some((p) => p.perm_name === permission);
  },

  hasRole: (roleName) => {
    return get().role?.role_name === roleName;
  },

  isAdmin: () => {
    return get().role?.role_type === "admin";
  },
}));

// ─── Selector hooks (back-compat) ─────────────────────────────

export const useUser = () => useAuthStore((s) => s.user);
export const useProfile = () => useAuthStore((s) => s.profile);
export const useRole = () => useAuthStore((s) => s.role);
export const usePermissions = () => useAuthStore((s) => s.permissions);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useIsAdmin = () => useAuthStore((s) => s.isAdmin());
